import React, { useState, useEffect } from "react";
import ImageRadioButton from "../../UIElements/ImageRadioButtons";
import FormButtonStyle4 from "../../UIElements/FormButton";
import FormInputStyle4 from "../../UIElements/FormInput";
import FormInputStyle5 from "../../UIElements/FormInput5";
import Header from "../Header/header";
import callApi from "../../../../utility/apiCaller";
import { useSelector } from "react-redux";

const DurationInput = ({
  years,
  months,
  onYearsChange,
  onMonthsChange,
  error,
}) => {
  return (
    <div className="flex gap-3 box-border w-full">
      <FormInputStyle5
        label="Years"
        type="text"
        value={years}
        onChange={onYearsChange}
        isValid={!error.years}
        errorMessage={"enter valid years"}
      />
      <FormInputStyle5
        label="Months"
        type="text"
        value={months}
        onChange={onMonthsChange}
        isValid={!error.months}
        errorMessage={"enter valid months (0-11)"}
      />
    </div>
  );
};

export default function ResidentialDetailsForm({
  setCurrentStep,
  setAddressState,
  // leadId = "68b16603596d4397307eccb3"
}) {
  const [formData, setFormData] = useState({
    residenceAddress: "",
    wingFloorFlat: "",
    buildingName: "",
    years: "",
    months: "",
    permanentAddressType: "Owned",
    permanentAddress: "",
    addressLine1: "",
    addressLine2: "",
    pincode: "",
  });
  const [ownership, setOwnership] = useState("Owned");
  const [pincodeValidation, setpincodeValidation] = useState(false);
  const user = useSelector((state) => state.app.user);
  const aaSlice = useSelector((state) => state?.aa);
  let leadId = aaSlice?.internalLeadId;
  const userData = aaSlice?.userData;
  const [isFormValid, setIsFormValid] = useState(false);
  const [years, setYears] = useState("");
  const [months, setMonths] = useState("");

  const [errors, setErrors] = useState({});

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handlePincodeChange = async (value) => {
    value = value.replace(/\D/g, ""); // allow only digits

    if (/^\d{0,6}$/.test(value)) {
      // Prevent first digit being 0 if there's at least one digit
      if (value.length > 0 && value[0] === "0") {
        return; // ignore input if first digit is 0
      }

      // ✅ always update input field
      setFormData((prev) => ({ ...prev, ["pincode"]: value }));

      // clear error if user is still typing
      if (errors.pincode) {
        setErrors((prev) => ({ ...prev, pincode: false }));
      }

      // Only validate when user has entered 6 digits
      if (value.length === 6) {
        setpincodeValidation(true); // start loading state

        try {
          // ✅ use your backend API with callApi
          const response = await callApi(
            `v1/util/${value}`, // endpoint
            "get",
            {},
            "core"
          );

          if (!response.success) {
            setErrors((prev) => ({ ...prev, pincode: true }));
          } else {
            setErrors((prev) => ({ ...prev, pincode: false }));

            // optional: auto-fill city field
            setFormData((prev) => ({
              ...prev,
              city: response.city || "", // assuming your API returns { success: true, city: "Amritsar" }
            }));
          }
        } catch (error) {
          console.error("Pincode validation error:", error);
          setErrors((prev) => ({ ...prev, pincode: true }));
        } finally {
          setpincodeValidation(false); // end loading state
        }
      }
    }
  };

  useEffect(() => {
    const allValid =
      formData.residenceAddress.trim() &&
      (!errors.residenceAddress || errors.residenceAddress === false) &&
      formData.wingFloorFlat.trim() &&
      (!errors.wingFloorFlat || errors.wingFloorFlat === false) &&
      formData.buildingName.trim() &&
      (!errors.buildingName || errors.buildingName === false) &&
      years &&
      (!errors.years || errors.years === false) &&
      months &&
      (!errors.months || errors.months === false) &&
      (!errors.years || errors.years === false) && // Optional if you have errors for years
      (!errors.months || errors.months === false) &&
      (formData.permanentAddressType !== "Rented" ||
        (formData.permanentAddress.trim() &&
          (!errors.permanentAddress || errors.permanentAddress === false) &&
          formData.addressLine1.trim() &&
          (!errors.addressLine1 || errors.addressLine1 === false) &&
          formData.pincode &&
          /^\d{6}$/.test(formData.pincode) &&
          (!errors.pincode || errors.pincode === false)));

    setIsFormValid(allValid);
  }, [
    formData.residenceAddress,
    errors.residenceAddress,
    formData.wingFloorFlat,
    errors.wingFloorFlat,
    formData.buildingName,
    errors.buildingName,
    years,
    errors.years,
    months,
    errors.months,
    formData.permanentAddressType,
    formData.permanentAddress,
    errors.permanentAddress,
    formData.addressLine1,
    errors.addressLine1,
    formData.pincode,
    errors.pincode,
  ]);

  const validateForm = () => {
    let newErrors = {};

    // Validate residence fields
    if (!formData.residenceAddress.trim())
      newErrors.residenceAddress = "Required";
    if (!formData.wingFloorFlat.trim()) newErrors.wingFloorFlat = "Required";
    if (!formData.buildingName.trim()) newErrors.buildingName = "Required";

    if (!years || isNaN(years) || years < 0) newErrors.years = "Invalid";
    if (!months || isNaN(months) || months < 0 || months > 11)
      newErrors.months = "Invalid (0-11)";

    // Validate permanent address fields only if ownership is "Rented"
    if (formData.permanentAddressType === "Rented") {
      if (!formData.permanentAddress.trim())
        newErrors.permanentAddress = "Required";
      if (!formData.addressLine1.trim()) newErrors.addressLine1 = "Required";
      if (!formData.pincode || !/^\d{6}$/.test(formData.pincode))
        newErrors.pincode = "Enter 6-digit pincode";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const onYearsChange = (e) => {
    let value = e.target.value;
    value = value.replace(/\D/g, ""); // allow only digits
    if (value.length > 3) {
      value = value.slice(0, 3);
    }

    setYears(value);

    // Validation: must be a non-negative number
    if (value === "") {
      setErrors((prev) => ({ ...prev, years: "Years cannot be empty" }));
    } else if (isNaN(value) || Number(value) < 0) {
      setErrors((prev) => ({
        ...prev,
        years: "Enter a valid non-negative number",
      }));
    } else {
      setErrors((prev) => ({ ...prev, years: false }));
    }
  };

  const onMonthsChange = (e) => {
    let value = e.target.value;
    value = value.replace(/[^0-9]/g, "");
    // allow only digits

    if (value.length > 2) {
      value = value.slice(0, 2);
    }
    if (value !== "") {
      const numericValue = parseInt(value, 10);
      if (numericValue > 11) {
        value = "11"; // cap months at 12
      }
    }

    setMonths(value);

    // Validation: must be between 0 and 12
    if (value === "") {
      setErrors((prev) => ({ ...prev, months: "Months cannot be empty" }));
    } else if (isNaN(value) || Number(value) < 0 || Number(value) > 11) {
      setErrors((prev) => ({
        ...prev,
        months: "Enter a valid number between 0 and 11",
      }));
    } else {
      setErrors((prev) => ({ ...prev, months: false }));
    }
  };

  const handleNext = async () => {
    if (!validateForm()) return;

    // Save to parent state
    setAddressState((prev) => ({
      ...prev,
      residenceAddress: formData.residenceAddress,
      residenceWingFloorFlat: formData.wingFloorFlat,
      residenceBuildingName: formData.buildingName,
      residenceYears: years,
      residenceMonths: months,
      residence_type: formData.permanentAddressType,
      permanentAddress: formData.permanentAddress,
      permanentaddressLine1: formData.addressLine1,
      permanentaddressLine2: formData.addressLine2,
      pincode: formData.pincode,
      address1:
        `${formData.residenceAddress} ${formData.wingFloorFlat} ${formData.buildingName}`.trim(),
      address2:
        `${formData.permanentAddress} ${formData.addressLine1} ${formData.addressLine2}`.trim(),
    }));

    console.log("formData", formData);

    await callApi(
      `v1/lead/${leadId}/update-lead-new`,
      "post",
      {
        lead: {
          is_residence_completed: true,
          steps_completed: 11,
        },
      },
      "core",
      user?.token ||
                 "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJfaWQiOiI2OTAxYjljZjIxZTZlNTJmYTQwNDAwNDkiLCJvdHAiOiIxMTExIiwiY29udGFjdF9waG9uZSI6Ijk2OTMxMzU0NjYiLCJvdHBfZXhwaXJ5IjoiMjAyNS0xMC0yOVQwNzowMzowMy43MDZaIiwiaWF0IjoxNzYxNzIwNzg3fQ.xJ3Qd29aQPzV6vfc-bm5XDaOyIF1dXI4dD3lRZxEBDo"
    );

    setCurrentStep((prev) => prev + 1);
  };

  return (
    <div className="flex flex-col min-h-[100dvh] font-atkinson w-full pb-2 overflow-hidden max-w-[400px] px-3 px-4-340">
      <Header />
      <h2 className="text-[20px] mt-3 ml-2 mb-2 font-atkinson-bold">
        Residential Details
      </h2>

      <FormInputStyle4
        label="Residence Address"
        value={formData.residenceAddress}
        onChange={handleChange("residenceAddress")}
        isValid={!errors.residenceAddress}
        error={errors.residenceAddress}
      />

      <FormInputStyle4
        label="Wing, Floor and Flat Number"
        value={formData.wingFloorFlat}
        onChange={handleChange("wingFloorFlat")}
        isValid={!errors.wingFloorFlat}
        error={errors.wingFloorFlat}
      />

      <FormInputStyle4
        label="Building Name"
        value={formData.buildingName}
        onChange={handleChange("buildingName")}
        isValid={!errors.buildingName}
        error={errors.buildingName}
      />

      <div className="px-4 py-2 w-full max-w-[340px] border-[1px] mx-auto rounded-[5px] border-[#B3B3B3]">
        <label className="text-sm font-medium block mb-2">
          How Long have you lived at Current Address?
        </label>
        <DurationInput
          years={years}
          error={errors}
          months={months}
          onYearsChange={onYearsChange}
          onMonthsChange={onMonthsChange}
        />
      </div>

      <div className="w-full p-3">
        <label className="text-sm font-medium block mb-2">
          Is this your Permanent Address?
        </label>
        <ImageRadioButton
          name="permanentAddressType"
          options={[
            { label: "Owned", value: "Owned" },
            { label: "Rented", value: "Rented" },
          ]}
          selectedValue={formData.permanentAddressType}
          onChange={(value) => {
            setFormData((prev) => ({ ...prev, permanentAddressType: value }));
            setOwnership(value);
          }}
        />
      </div>

      {ownership && ownership === "Rented" && (
        <div>
          <FormInputStyle4
            label="Type your Permanent Address"
            value={formData.permanentAddress}
            onChange={handleChange("permanentAddress")}
            isValid={!errors.permanentAddress}
            error={errors.permanentAddress}
          />

          <FormInputStyle4
            label="Address Line 1"
            value={formData.addressLine1}
            onChange={handleChange("addressLine1")}
            isValid={!errors.addressLine1}
            error={errors.addressLine1}
          />

          <FormInputStyle4
            label="Address Line 2"
            value={formData.addressLine2}
            onChange={handleChange("addressLine2")}
            isValid={true}
          />

          {/* <FormInputStyle4
            label="Pincode"
            value={formData.pincode}
            onChange={handleChange("pincode")}
            isValid={!errors.pincode}
            error={errors.pincode}
            tick={!errors.pincode && formData.pincode.length === 6}
          /> */}
          <FormInputStyle4
            label="Pincode"
            value={formData.pincode}
            onChange={(e) => handlePincodeChange(e.target.value)}
            onBlur={() => {
              if (!/^\d{6}$/.test(formData.pincode) || errors.pincode) {
                setErrors((prev) => ({ ...prev, pincode: true }));
              }
            }}
            isValid={!errors.pincode}
            errorMessage="Enter a valid 6-digit PIN"
            tick={formData.pincode.length === 6 && !errors.pincode}
            loading={pincodeValidation}
          />
        </div>
      )}
      <FormButtonStyle4
        className="mt-auto mb-3"
        text="Next"
        onClick={handleNext}
        disabled={!isFormValid || pincodeValidation}
      />
    </div>
  );
}
